import { Link } from "@tanstack/react-router"
import type { ReactNode } from "react"
import { PageHeader } from "@/components/shell/page-header"
import { Screen } from "@/components/ui/screen"
import { type MessageKey, useT } from "@/lib/i18n"
import { useScope } from "@/lib/scope"
import { HowItWorks } from "@/workspace/how-it-works"

/** The Data workspace's page frame: one breadcrumb root, a title, a quiet explanation and the ⓘ. */
export function DataFrame({
  title,
  description,
  how,
  crumbs = [],
  actions,
  width = "board",
  children,
}: {
  title: ReactNode
  description?: ReactNode
  how: readonly MessageKey[]
  crumbs?: readonly ReactNode[]
  actions?: ReactNode
  width?: "wide" | "board" | "full"
  children: ReactNode
}) {
  const t = useT()
  const scope = useScope()
  return (
    <>
      <PageHeader
        crumbs={[
          <Link key="data" to="/$orgId/$environmentId/data" params={scope}>
            {t("dv.nav.data")}
          </Link>,
          ...crumbs,
        ]}
        actions={
          <>
            {actions}
            <HowItWorks lines={how} />
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width={width} className="space-y-8 px-4 py-8 md:px-8">
          <header className="space-y-1">
            <h1 className="text-[1.75rem] leading-tight font-semibold tracking-[-0.02em]">{title}</h1>
            {description ? <p className="max-w-3xl text-sm text-muted-foreground">{description}</p> : null}
          </header>
          {children}
        </Screen>
      </div>
    </>
  )
}

/** A compact labelled number for summary rows. */
export function Stat({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold tracking-tight tabular-nums">{value}</p>
      {hint ? <p className="truncate text-2xs text-subtle-foreground">{hint}</p> : null}
    </div>
  )
}
